<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from '@/plugins/i18n';
import { useSearchStore } from '../../store/index.ts';
import FilterDropdown from '../../FilterDropdown.ce.vue';
import CategoriesFilter from '@/components/Search/SearchFilters/Common/CategoriesFilter/index.ce.vue';

import iconStatus from '@/assets/img/icon-search-all-filters-status.svg';
import iconPublisher from '@/assets/img/icon-search-all-filters-publisher.svg';
import iconSearchIn from '@/assets/img/icon-search-all-filters-search-in.svg';
import iconCountry from '@/assets/img/icon-search-all-filters-country.svg';
import iconDataset from '@/assets/img/icon-search-all-filters-dataset.svg';
import iconLanguage from '@/assets/img/icon-search-all-filters-language.svg';
import iconYear from '@/assets/img/icon-search-all-filters-year.svg';
import iconInfo from '@/assets/img/icon-info.svg';
import type { SearchFilterOption } from '@/components/Search/types.ts';
import { STANDARDS_SEARCH_TAB } from '@/components/Search/constants.ts';
import CrossReferenceSuggest from '@/components/Search/CrossReferenceSuggest.ce.vue';
import SaiTooltip from '@/components/common/SaiTooltip/index.ce.vue';

const props = defineProps({
    searchOnSelect: { type: Boolean, default: true },
    forMobile: { type: Boolean, default: false }
})

//ref 
const showSearchInTooltip = ref(false); 
const tooltipSearchIn = ref<HTMLElement | null>(null);

//store
const searchStore = useSearchStore()
const updateSelectedItems = (items: SearchFilterOption[]) => {
  if(props.searchOnSelect) {
    searchStore.searchStandards(true);
  }
}
//translations
const i18nInstance: any = useI18n()
const { t } = i18nInstance

const onCrossRefSelect = (value: string) => {
  searchStore[`${STANDARDS_SEARCH_TAB}.facetFilter.crossReference`] = value;
  if(props.searchOnSelect) {
      searchStore.searchStandards(true);
  }
};
const crossRefCss = props.forMobile ? 'crossref-input crossref-input--modal' : 'crossref-input crossref-input--facet';
const crossRefOverLayPlacement = props.forMobile ? 'full' : 'relative';
</script>
<template>
  <div class="standards-filters">
    <div class="filter-item cross-reference">
      <CrossReferenceSuggest
        v-model="searchStore[`${STANDARDS_SEARCH_TAB}.facetFilter.crossReference`]"
        :placeholder="t('crossReferencePlaceHolder')"
        :matchInputWidth="true"
        :useFixedOverlay="false"
        :dropdownExtraWidth="20"
        :invertClearOnBlur="false"
        :inputClass="crossRefCss"
        :overlayPlacement="crossRefOverLayPlacement"
        @select="onCrossRefSelect"
      />
    </div>
    <div class="filter-item search-in">
      <FilterDropdown
        className="accordion"
        :placeholder="t('searchIn')"
        name="searchIn"
        :isOpen="searchStore[`${STANDARDS_SEARCH_TAB}.facetFilter.searchIn.expanded`]"
        :multiple="false"
        :field="`${STANDARDS_SEARCH_TAB}.facetFilter.searchIn`"
        :lookupField="`lookUpOptions.${STANDARDS_SEARCH_TAB}.facetFilter.searchIn`"
        :defaultOptionLookupField="`lookUpOptions.${STANDARDS_SEARCH_TAB}.searchIn`"
        :hasClear="false"
        :iconMain="iconSearchIn"
        :iconOpenMain="iconSearchIn"
        :searchable="false"
        @updateSelectedItems="updateSelectedItems">
        <template #info>
          <div class="searchin-info">
            <img :src="iconInfo"
            ref="tooltipSearchIn" 
            @mouseenter="showSearchInTooltip = true" 
            @mouseleave="showSearchInTooltip = false"/>
            <SaiTooltip
              v-if="tooltipSearchIn"
              v-model="showSearchInTooltip"
              :target="tooltipSearchIn"
              :placement="props.forMobile ? 'bottom': 'right'"
              :offset="8"
              :showArrow="false"
            >
              <div class="searchin-tooltip">
                {{ t('searchInToolTip') }}
                <ul>
                  <li class="click2view"><span class="label">{{ t('click2ViewLabel') }}:</span>&nbsp;{{ t('onlineViewOrDownloadAccess') }}</li>
                  <li class="watch">{{ t('standardsWatching') }}</li>
                  <li class="collections">{{ t('itemsInCollection') }}</li>
                </ul>
              </div>
            </SaiTooltip>
          </div>
          
        </template>
      </FilterDropdown>
    </div>
    <div class="filter-item status">
      <FilterDropdown
        className="accordion"
        :placeholder="t('status')"
        name="status"
        :multiple="true"
        :field="`${STANDARDS_SEARCH_TAB}.facetFilter.status`"
        :lookupField="`lookUpOptions.${STANDARDS_SEARCH_TAB}.facetFilter.status`"
        :defaultOptionLookupField="`lookUpOptions.${STANDARDS_SEARCH_TAB}.status`"
        :hasClear="false"
        :iconMain="iconStatus"
        :iconOpenMain="iconStatus"
        :searchable="false"
        @updateSelectedItems="updateSelectedItems"/>
    </div>
    <div class="filter-item publisher">
      <FilterDropdown
        className="accordion"
        :placeholder="t('publisher')"
        :searchPlaceholder="t('searchPublisherPlaceholder')"
        name="publisher[]"
        :multiple="true"
        :field="`${STANDARDS_SEARCH_TAB}.facetFilter.publisher`"
        :lookupField="`lookUpOptions.${STANDARDS_SEARCH_TAB}.facetFilter.publisher`"
        :defaultOptionLookupField="`lookUpOptions.${STANDARDS_SEARCH_TAB}.publisher`"
        :searchable="true"
        :hasClear="false"
        :iconMain="iconPublisher"
        :iconOpenMain="iconPublisher"
        :loadOptionsOnClick="true"
        @updateSelectedItems="updateSelectedItems"
      />
    </div>
    <div class="filter-item country">
      <FilterDropdown
        className="accordion"
        :placeholder="t('country')"
        name="country"
        :multiple="true"
        :field="`${STANDARDS_SEARCH_TAB}.facetFilter.country`"
        :lookupField="`lookUpOptions.${STANDARDS_SEARCH_TAB}.facetFilter.country`"
        :defaultOptionLookupField="`lookUpOptions.${STANDARDS_SEARCH_TAB}.country`"
        :hasClear="false"
        :iconMain="iconCountry"
        :iconOpenMain="iconCountry"
        :searchable="true"
        :loadOptionsOnClick="true"
        @updateSelectedItems="updateSelectedItems"/>
    </div>
    <div class="filter-item dataset">
      <FilterDropdown
        className="accordion"
        :placeholder="t('dataset')"
        name="dataset"
        :multiple="true"
        :field="`${STANDARDS_SEARCH_TAB}.facetFilter.dataset`"
        :lookupField="`lookUpOptions.${STANDARDS_SEARCH_TAB}.facetFilter.dataset`"
        :defaultOptionLookupField="`lookUpOptions.${STANDARDS_SEARCH_TAB}.dataset`"
        :hasClear="false"
        :iconMain="iconDataset"
        :iconOpenMain="iconDataset"
        :searchable="false"
        @updateSelectedItems="updateSelectedItems"/>
    </div>
    <div class="filter-item categories">
      <CategoriesFilter :searchType="STANDARDS_SEARCH_TAB" 
      className="accordion" :facetFilter="true"
      :useTeleport="!forMobile"></CategoriesFilter>
    </div>
    <div class="filter-item language">
      <FilterDropdown
        className="accordion"
        :placeholder="t('language')"
        name="language"
        :multiple="true"
        :field="`${STANDARDS_SEARCH_TAB}.facetFilter.language`"
        :lookupField="`lookUpOptions.${STANDARDS_SEARCH_TAB}.facetFilter.language`"
        :defaultOptionLookupField="`lookUpOptions.${STANDARDS_SEARCH_TAB}.language`"
        :hasClear="false"
        :iconMain="iconLanguage"
        :iconOpenMain="iconLanguage"
        :searchable="false"
        @updateSelectedItems="updateSelectedItems"/>
    </div>
    <div class="filter-item year">
      <FilterDropdown
        className="accordion"
        :placeholder="t('year')"
        name="year"
        :multiple="true"
        :field="`${STANDARDS_SEARCH_TAB}.facetFilter.year`"
        :lookupField="`lookUpOptions.${STANDARDS_SEARCH_TAB}.facetFilter.year`"
        :defaultOptionLookupField="`lookUpOptions.${STANDARDS_SEARCH_TAB}.year`"
        :hasClear="false"
        :iconMain="iconYear"
        :iconOpenMain="iconYear"
        :searchable="false"
        @updateSelectedItems="updateSelectedItems"/>
    </div>
  </div>
</template>
